import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Shield, Brain, TrendingUp, Eye, Zap,
  CheckCircle, ArrowRight, Play, HardHat,
  AlertTriangle, BarChart3, Lock, Clock, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const features = [
  { icon: Brain, title: 'AI 실시간 분석', description: 'YOLOv8 기반 실시간 객체 감지로 위험 상황을 즉시 포착합니다.', color: 'text-primary', bgColor: 'bg-primary/10' },
  { icon: Shield, title: 'PPE 자동 감지', description: '안전모, 안전조끼 등 보호장비 착용 여부를 자동으로 확인합니다.', color: 'text-success', bgColor: 'bg-success/10' },
  { icon: AlertTriangle, title: '위험구역 모니터링', description: '출입제한 구역 진입 시 실시간 알람과 영상 녹화를 진행합니다.', color: 'text-warning', bgColor: 'bg-warning/10' },
  { icon: Eye, title: 'PTZ 자동 추적', description: '위험 상황 발생 시 PTZ 카메라가 자동으로 추적하고 기록합니다.', color: 'text-info', bgColor: 'bg-info/10' },
  { icon: BarChart3, title: '통합 리포트', description: '일/주/월 단위 안전 통계와 AI 분석 결과를 한눈에 확인합니다.', color: 'text-gray-70', bgColor: 'bg-gray-10' },
  { icon: Zap, title: '즉시 알림', description: '사고 발생 즉시 담당자에게 푸시 알림과 SMS를 발송합니다.', color: 'text-accent', bgColor: 'bg-accent/10' },
];

const stats = [
  { label: '사고 예방률', value: '94%', icon: Shield, color: 'text-success' },
  { label: '실시간 모니터링', value: '24/7', icon: Clock, color: 'text-primary' },
  { label: '분석 정확도', value: '98.2%', icon: Brain, color: 'text-info' },
  { label: '적용 현장', value: '150+', icon: Users, color: 'text-warning' },
];

const useCases = [
  { id: 'safety', title: '안전 관리', icon: HardHat, items: ['PPE 미착용 자동 감지', '위험구역 출입 모니터링', '낙하물 위험 예측', '작업자 행동 분석'] },
  { id: 'security', title: '보안 관리', icon: Lock, items: ['무단 출입 감지', '차량 번호판 인식', '근무 시간 외 모니터링', '자재 도난 방지'] },
  { id: 'efficiency', title: '효율성 향상', icon: TrendingUp, items: ['작업 진행도 자동 추적', '장비 가동률 모니터링', '인력 배치 최적화', '생산성 분석 리포트'] },
];

export function LandingPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('safety');

  const goToDashboard = () => navigate('/dashboard');

  return (
    <div className="min-h-screen bg-gray-0">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-gray-20 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <Camera className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-gray-90 text-lg font-bold">SafetyVision AI</h1>
                <p className="text-xs text-gray-60">건설현장 AI 안전관리 솔루션</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" className="text-gray-70 hover:text-gray-90" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>제품 소개</Button>
              <Button variant="ghost" className="text-gray-70 hover:text-gray-90" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>가격</Button>
              <Button variant="ghost" className="text-gray-70 hover:text-gray-90" onClick={() => document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' })}>고객 사례</Button>
              <Button onClick={goToDashboard} className="bg-primary hover:bg-primary-hover shadow-md">
                대시보드 체험 <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-gray-5 to-white">
        <div className="container mx-auto text-center">
          <Badge className="bg-primary/10 text-primary border-primary/20 mb-6">
            <Zap className="w-3 h-3 mr-1" /> AI 기반 스마트 안전관리
          </Badge>
          <h1 className="text-5xl md:text-6xl text-gray-90 font-bold mb-6 leading-tight">
            건설현장의 안전을<br />
            <span className="text-primary">AI가 지켜드립니다</span>
          </h1>
          <p className="text-xl text-gray-60 mb-10 max-w-2xl mx-auto">
            실시간 영상 분석으로 사고를 예방하고, 현장 안전을 강화하는 차세대 CCTV 관제 시스템
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" onClick={goToDashboard} className="bg-primary hover:bg-primary-hover text-lg h-14 px-8 shadow-lg w-full sm:w-auto">
              <Play className="w-5 h-5 mr-2" /> 무료 데모 체험
            </Button>
            <Button size="lg" variant="outline" className="text-lg h-14 px-8 border-gray-30 w-full sm:w-auto">
              제품 문의하기
            </Button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20 max-w-4xl mx-auto">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <Card key={i} className="p-6 bg-background border-gray-20 shadow-sm hover:shadow-md transition-shadow">
                  <Icon className={`w-8 h-8 ${stat.color} mx-auto mb-3`} />
                  <div className="text-3xl font-bold text-gray-90 mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-60">{stat.label}</div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo Video */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-5xl">
          <div className="aspect-video bg-gradient-to-br from-gray-10 to-gray-5 rounded-2xl border-2 border-gray-20 overflow-hidden shadow-xl flex items-center justify-center">
            <div className="text-center">
              <button disabled aria-label="시스템 데모 영상 재생" className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 opacity-50 cursor-not-allowed transition-colors shadow-lg">
                <Play className="w-10 h-10 text-white ml-1" />
              </button>
              <p className="text-gray-60">시스템 데모 영상 보기</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 px-4 bg-gray-5">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">핵심 기능</Badge>
            <h2 className="text-4xl font-bold text-gray-90 mb-4">AI가 만드는 안전한 건설현장</h2>
            <p className="text-xl text-gray-60 max-w-2xl mx-auto">첨단 AI 기술로 현장의 모든 위험 요소를 실시간으로 감지하고 대응합니다</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card key={i} className="bg-background border-gray-20 p-6 hover:border-primary hover:shadow-lg transition-all cursor-pointer group">
                  <div className={`w-14 h-14 ${f.bgColor} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-7 h-7 ${f.color}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-90 mb-2">{f.title}</h3>
                  <p className="text-gray-60">{f.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <Badge className="bg-primary/10 text-primary border-primary/20 mb-4">활용 사례</Badge>
            <h2 className="text-4xl font-bold text-gray-90 mb-4">다양한 현장에서 검증된 솔루션</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              return (
                <Button key={uc.id} variant={activeTab === uc.id ? 'default' : 'outline'} onClick={() => setActiveTab(uc.id)} className="h-12">
                  <Icon className="w-4 h-4 mr-2" /> {uc.title}
                </Button>
              );
            })}
          </div>
          <Card className="bg-gray-5 border-gray-20 p-8 shadow-md">
            {useCases.map((uc) => {
              const Icon = uc.icon;
              if (activeTab !== uc.id) return null;
              return (
                <div key={uc.id}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-90">{uc.title}</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {uc.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-3 p-4 bg-background rounded-lg border border-gray-20">
                        <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                        <span className="text-gray-80">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="py-20 px-4 bg-gradient-to-r from-primary/5 via-blue-50 to-primary/5">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-background border-primary/20 p-12 text-center shadow-xl">
            <h2 className="text-4xl font-bold text-gray-90 mb-4">지금 바로 시작하세요</h2>
            <p className="text-xl text-gray-60 mb-8">무료 데모로 SafetyVision AI의 강력한 기능을 직접 체험해보세요</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" onClick={goToDashboard} className="bg-primary hover:bg-primary-hover text-lg h-14 px-8 shadow-lg w-full sm:w-auto">
                <Play className="w-5 h-5 mr-2" /> 대시보드 체험하기
              </Button>
              <Button size="lg" variant="outline" className="text-lg h-14 px-8 w-full sm:w-auto">영업팀 상담</Button>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-20 py-12 px-4 bg-gray-5">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-blue-600 rounded-lg flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-gray-90">SafetyVision AI</span>
              </div>
              <p className="text-gray-60 text-sm">건설현장 안전을 위한 AI 솔루션</p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-90 mb-3">제품</h4>
              <ul className="space-y-2 text-gray-60 text-sm">
                <li>기능 소개</li>
                <li>가격 정책</li>
                <li>데모 체험</li>
                <li>업데이트</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-90 mb-3">고객 지원</h4>
              <ul className="space-y-2 text-gray-60 text-sm">
                <li>문서</li>
                <li>API</li>
                <li>FAQ</li>
                <li>문의하기</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-90 mb-3">회사</h4>
              <ul className="space-y-2 text-gray-60 text-sm">
                <li>회사 소개</li>
                <li>채용</li>
                <li>블로그</li>
                <li>파트너</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-20 pt-8 text-center text-gray-50 text-sm">
            &copy; {new Date().getFullYear()} SafetyVision AI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
